<template>
  <v-dialog :value="open" :width="1024" persistent>
    <v-card>
      <v-card-title primary-title class="title">{{ $t('manage_facebook_template') }}</v-card-title>
      <form id="facebookTemplateForm" @submit="handleSubmit">
        <v-card-text>
          <v-container fluid grid-list-lg class="pa-0" :style="{ width: '100% !important' }">
            <v-layout wrap>
              <v-flex xs12 sm6 class="form-control" :class="{ 'has-error': errors.has('name') }">
                <label>{{ $t('name') }}</label>
                <input
                  v-model="form.name"
                  v-validate="'required'"
                  type="text"
                  name="name"
                  :data-vv-as="$t('name')"
                />
                <span v-if="errors.has('name')" class="text-error">{{ errors.first('name') }}</span>
              </v-flex>
              <v-flex
                xs12
                sm6
                class="form-control"
                :class="{ 'has-error': errors.has('send_type') }"
              >
                <label>{{ $t('template_type') }}</label>
                <v-layout wrap align-center>
                  <v-flex class="py-0">
                    <multiselect
                      v-model="form.send_type"
                      v-validate="'required'"
                      name="send_type"
                      :options="typeOptions"
                      :custom-label="(value) => $t(value)"
                      :searchable="false"
                      close-on-select
                      :data-vv-as="$t('template_type')"
                    ></multiselect>
                  </v-flex>
                  <v-flex shrink class="py-0">
                    <v-btn
                      icon
                      flat
                      color="warning"
                      href="https://developers.facebook.com/docs/messenger-platform/reference/send-api#message-tag-usages"
                      target="_blank"
                      class="ma-0"
                    >
                      <v-icon>help</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
                <span v-if="errors.has('send_type')" class="text-error">{{
                  errors.first('send_type')
                }}</span>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          <h4 class="subheading">{{ $t('bot_answers') }}</h4>
          <v-container fluid grid-list-lg class="pa-0" :style="{ width: '100% !important' }">
            <v-layout wrap>
              <v-flex xs12 sm6>
                <IntentGroup
                  :key="refreshTimestamp"
                  v-model="form.message_value"
                  :has-alternative="false"
                  :is-welcome="true"
                  :available-bubbles="['text', 'image']"
                ></IntentGroup>
              </v-flex>
              <v-flex xs12 sm6>
                <CustomWording v-model="form.custom_word"></CustomWording>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </form>
      <v-card-actions class="justify-center">
        <v-btn outline color="grey" :disabled="isLoading" @click="$emit('update:open', false)">{{
          $t('cancel')
        }}</v-btn>
        <v-btn type="submit" form="facebookTemplateForm" color="primary" :loading="isLoading">{{
          $t('Save')
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import facebookTemplateApi from '@/api/facebook-template'
import IntentGroup from '@/components/view/intent/IntentGroup'
import Multiselect from 'vue-multiselect'
import { mapGetters } from 'vuex'
import CustomWording from './CustomWording.vue'

const defaultForm = {
  name: '',
  send_type: null,
  message_value: [[]],
  message_type: '',
  is_active: true,
  custom_word: []
}

export default {
  components: {
    Multiselect,
    IntentGroup,
    CustomWording
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: structuredClone(defaultForm),
      refreshTimestamp: Date.now(),
      typeOptions: [
        'ACCOUNT_UPDATE',
        'CONFIRMED_EVENT_UPDATE',
        'CUSTOMER_FEEDBACK',
        'HUMAN_AGENT',
        'POST_PURCHASE_UPDATE'
      ],
      isLoading: false
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    })
  },
  watch: {
    'form.message_value.0'(values) {
      const types = values.map((value) => value.type)
      this.form.message_type = types.length > 1 ? 'json' : types.length === 1 ? types[0] : ''
    }
  },
  created() {
    this.$watch(() => [this.open, this.value], this.handleFormInit)
  },
  methods: {
    handleFormInit([open, value]) {
      if (!open) return

      if (value) {
        this.form = {
          name: value.name,
          send_type: value.send_type,
          message_value: [structuredClone(value.message_value)],
          message_type: value.message_type,
          is_active: value.is_active,
          custom_word: value.custom_word.map((word) => word.text)
        }
      } else {
        this.form = structuredClone(defaultForm)
      }

      this.refreshTimestamp = Date.now()
      this.$validator.reset()
    },
    async handleSubmit(event) {
      event.preventDefault()

      const result = await this.$validator.validateAll()
      if (!result) {
        this.goToErrorInput()
        return
      }

      try {
        this.isLoading = true

        const payload = {
          page_id: this.selectedBot.pageid,
          name: this.form.name,
          send_type: this.form.send_type,
          message_value: this.form.message_value[0],
          message_type: this.form.message_type,
          is_active: this.form.is_active,
          custom_word: this.form.custom_word.map((word, wordIndex) => ({
            position: `{{${wordIndex + 1}}}`,
            text: word
          }))
        }

        let response

        if (this.value) {
          response = await facebookTemplateApi.put({
            data: {
              id: this.value.id,
              ...payload
            }
          })
        } else {
          response = await facebookTemplateApi.post({
            data: payload
          })
        }

        if (response.status === 204) {
          this.$emit('submitted')
        }
      } catch (error) {
        if (error.response) {
          this.$swal.fire(error.response.data.message, undefined, 'error')
        }
        console.error(error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/view/facebook-template/FacebookTemplateFormDialog.vue